/**
 * Hook for voice recording and transcription
 * Router/Facade pattern: uses native Web Speech API on mobile/Mac, Whisper on Windows
 */
import { useState, useEffect, useCallback } from 'react';
import { shouldUseNativeSpeech } from '../utils/deviceDetection';
import { useWebSpeechRecording } from './useWebSpeechRecording';
import { useWhisperRecording } from './useWhisperRecording';

interface UseVoiceRecordingResult {
  isRecording: boolean;
  isTranscribing: boolean;
  isWarming: boolean;
  error: string | null;
  audioLevel: number;
  waveformData: number[];
  interimTranscript?: string; // Only available with Web Speech API
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<string | null>;
}

export function useVoiceRecording(): UseVoiceRecordingResult {
  const [useNative, setUseNative] = useState(() => shouldUseNativeSpeech());

  // Update on window resize (device orientation changes)
  useEffect(() => {
    const handleResize = () => {
      setUseNative(shouldUseNativeSpeech());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize both hooks
  const webSpeech = useWebSpeechRecording();
  const whisper = useWhisperRecording();

  // Select the appropriate implementation
  const implementation = useNative ? webSpeech : whisper;

  // Wrapper for startRecording to handle Web Speech API differences
  const startRecording = useCallback(async () => {
    if (useNative) {
      // Web Speech API is synchronous
      webSpeech.startRecording();
      return Promise.resolve();
    } else {
      // Whisper API is async
      return whisper.startRecording();
    }
  }, [useNative, webSpeech, whisper]);

  // Wrapper for stopRecording to handle Web Speech API differences
  const stopRecording = useCallback(async (): Promise<string | null> => {
    if (useNative) {
      // Web Speech API returns transcript immediately
      webSpeech.stopRecording();
      return Promise.resolve(webSpeech.transcript || null);
    } else {
      // Whisper API transcribes after recording stops
      return whisper.stopRecording();
    }
  }, [useNative, webSpeech, whisper]);

  return {
    isRecording: implementation.isRecording,
    isTranscribing: useNative ? false : whisper.isTranscribing,
    isWarming: useNative ? false : whisper.isWarming,
    error: implementation.error,
    audioLevel: useNative ? 0 : whisper.audioLevel,
    waveformData: useNative ? [] : whisper.waveformData,
    interimTranscript: useNative ? webSpeech.interimTranscript : undefined,
    startRecording,
    stopRecording,
  };
}
